import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import JuanEstebanPhoto from "../candidates/JuanEsteban.jpg"

export default function ContentProducerRoleJuanEsteban() {
  const candidateProfileCopy = {
    name: "Juan Esteban",
    location: "Bogotá, Colombia",
    compensation: {
      range: "$15 USD / hourly",
    },
    availability: "Available to start immediately",

    working_style:
      "Juan Esteban is a strategic communicator and content systems builder who approaches every content brief as a lead generation problem first, a production problem second. He structures content around narrative frameworks — hook, relatable problem, transformation, solution — and wires each piece to a clear conversion channel (landing page, WhatsApp, DM, form). He is highly organized, comfortable coordinating remote and multicultural teams, and fluent in the full content operations stack from research to publishing to analytics. He uses AI tools selectively and evaluates them critically, preferring high-quality video generation platforms for their prompt-following accuracy. His 9 years of experience span audiovisual production, strategic communications, event coordination, copywriting, and digital marketing — making him one of the most well-rounded profiles in this candidate group.",

    loom_video_link: "https://fathom.video/share/4_yyzfuNsBzERezjo3krkDfnvQA5H4DX",

    tools: [
      {
        category: "Content Strategy & Lead Generation",
        items: [
          "Three-layer-la content aaframework: Attention → Authority → Trust/Conversion",
          "Trying-Narrative structure for lead-driven video: hook + relatable problem + transformation + solution",
          "Topic research via Zoho Social (keyword + viral + lead-quality filtering)",
          "YouTube hashtag trending and Meta platform topic research",
          "Competitor content analysis filtered by lead conversion, not just engagement",
          "Google Analytics and Meta Analytics for funnel drop-off diagnosis",
        ],
      },
      {
        category: "Platforms & Publishing Ops",
        items: [
          "Meta Business Suite (advanced: scheduling, analytics, performance optimization)",
          "YouTube Studio Creator (analytics, monetization, performance)",
          "Instagram, Facebook, YouTube (professional multi-platform management)",
          "Zoho One ecosystem (Zoho Social, Mail, Cliq, Meetings, Survey, Projects, Sprints)",
          "WordPress (content management, web data entry, migration)",
          "Google Workspace (Docs, Sheets, Drive, Forms — project coordination)",
          "Trello (project and content planning)",
        ],
      },
      {
        category: "Video Production & Editing",
        items: [
          "Adobe Premiere Pro (professional video editing)",
          "Adobe Audition (audio editing and finishing)",
          "OBS Studio (live streaming and broadcast)",
          "Wirecast (professional live production)",
          "YouTube Studio (live streaming, short-form, Reels, Shorts)",
          "Short-form video: Reels, Shorts, Stories (4+ years continuous production)",
          "Long-form: full-length programs, live broadcasts, event coverage",
          "Post-production: rundowns, technical scripts, optimized delivery per platform",
        ],
      },
      {
        category: "AI Tools & Generative Content",
        items: [
          "ChatGPT Plus (strategic prompting and content creation)",
          "Envato AI Labs (high-quality AI video generation, near-4K)",
          "MidJourney (image generation)",
          "Kling (AI video generation)",
          "Sora (AI video, used selectively)",
          "Gemini (AI content support)",
          "AI for content automation, process optimization, and creative research",
        ],
      },
      {
        category: "Copywriting & Communications",
        items: [
          "Scriptwriting and storyboarding",
          "Copywriting adapted to specific audiences (ES/EN)",
          "Institutional and strategic communications",
          "Public relations and stakeholder messaging",
          "Bilingual content production (English/Spanish)",
        ],
      },
      {
        category: "Live Streaming & Event Production",
        items: [
          "Technical direction of live broadcasts (OBS, Wirecast, Zoom, YouTube Studio)",
          "Digital, hybrid, and in-person event coordination (national and international)",
          "Rundowns, technical scripts, and multi-platform delivery",
          "Multimedia content management (Envato Elements, Adobe Stock)",
        ],
      },
    ],

    soft_skills: [
      "9 years of strategic communications and audiovisual production experience",
      "Systems thinker: structures content around lead outcomes, not just formats",
      "Bilingual (English/Spanish) — professional use in international environments",
      "Strong narrative and storytelling instincts for conversion-driven content",
      "Organized and self-directed in remote, multicultural team environments",
      "Performance-aware: diagnoses content using analytics, not intuition alone",
      "Proactive ownership mindset: plans 90-day execution frameworks independently",
      "Experience coordinating and leading small production teams",
    ],

    about_candidate:
      "Juan Esteban is a communications and audiovisual production professional with 9 years of experience spanning content strategy, digital marketing, copywriting, video production, and live streaming. He approaches every content brief as a lead generation problem: what story stops the scroll, builds trust, and moves someone to act. He has driven measurable results — including a content-led campaign that brought 4,000 live event attendees against a goal of 3,500 — and has managed continuous multi-platform content operations for real organizations for 5+ years.",

    core_strengths: [
      {
        headline: "Narrative-driven lead generation framework",
        description:
          "Structures content around a proven conversion sequence: hook → relatable problem → transformation → solution → CTA. Applies this framework to short-form video, Reels, and social posts to move audiences from scroll to inquiry.",
      },
      {
        headline: "Content-to-conversion analytics and funnel diagnosis",
        description:
          "Uses Google Analytics and Meta Analytics to trace where leads drop off between content and conversion — and has identified real funnel failures from live campaign experience. Closes the loop between content performance and lead outcomes.",
      },
      {
        headline: "SEO topic research filtered by lead quality",
        description:
          "Uses Zoho Social for keyword discovery but screens content by lead conversion potential, not just virality — explicitly excluding high-reach, low-conversion references. Directly aligned with producing content that attracts qualified project inquiries, not just views.",
      },
      {
        headline: "5+ years of continuous content engine operations",
        description:
          "Has managed weekly multi-platform publishing — full-length programs, live broadcasts, Reels, Shorts, and promotional content — continuously across YouTube, Facebook, and Instagram for a real organization. This is operational content engine management, not campaign-level experience.",
      },
    ],

    relevant_experience: [
      {
        headline: "Communication Strategist & Project Manager | Kenneth Copeland Ministries | 2023–2025",
        bullets: [
          "Communication strategy for Latin America: copywriting, digital coordination, and institutional messaging",
          "Led comprehensive event coordination: production, logistics, speaker management, and public relations",
          "Coordinated suppliers and setup of a TV studio for a regional office",
          "Produced short-form Reels and Shorts for a bilingual audience across YouTube, Facebook, and Instagram",
        ],
      },
      {
        headline: "Director of Audiovisual Production & Communications | Iglesia Palabras de Fe y Vida | 2020–Present",
        bullets: [
          "Direction and operation of live broadcasts, digital and hybrid events",
          "Full-length programs, short-form Reels/Shorts, event coverage, and promotional content",
          "Continuous multi-platform publishing: YouTube, Facebook, Instagram (5+ years)",
          "Project management with Trello, OBS, Wirecast, and Google Workspace",
        ],
      },
      {
        headline: "Video Editor | The Creators Marketplace | 2025",
        bullets: [
          "Recording, editing, and post-production of videos for social media and digital campaigns",
          "Audiovisual content focused on marketing and brand positioning",
        ],
      },
      {
        headline: "Video Producer | Linka AI | 2025",
        bullets: [
          "Production and editing of a product launch video",
          "100% remote project coordinated in English with an international team",
          "Editing, material sourcing, assembly, transitions, and post-production",
        ],
      },
      {
        headline: "Audiovisual Producer | Kenneth Copeland Ministries | 2020–2021",
        bullets: [
          "Production of audiovisual pieces for campaigns across Latin America",
        ],
      },
      {
        headline: "Web Assistant | Kenneth Copeland Ministries | 2016",
        bullets: [
          "WordPress web data entry, content migration, and content management",
        ],
      },
      {
        headline: "Communications Volunteer | Colombian Council for Sustainable Construction (Construverde) | 2019",
        bullets: [
          "Managed social media publications, conducted speaker interviews, and photographed an international sustainable design forum",
        ],
      },
    ],
  };

  // ─── PAGE 2 DATA ─────────────────────────────────────────────────────────────
  const technicalAssessment = {
    meta: {
      role: "Content Producer — Handy Manny's Remodeling",
      interviewDate: "March 06, 2026",
      analyst: "AI-Architechs Senior Technical Hiring Analysis",
    },

    snapshot:
      "Juan Esteban is a well-rounded content and communications professional with 9 years of experience spanning audiovisual production, strategic copywriting, social media management, and content lead generation — a profile that sits comfortably between Daniel's pure production depth and Lihué's pure strategy depth. He thinks in content systems, speaks fluently about narrative structure and lead generation mechanics, references real tools for SEO-driven topic research, and has actually shipped content that drove measurable outcomes (4,000 event attendees). His risk is that his deepest experience is in the non-profit/ministry context rather than commercial lead-generation for local services, and his AI video toolchain, while present, is less developed than Daniel's. He is a strong choice if you want one hire who can hold both the strategy and production layers simultaneously at a solid, if not elite, level on either dimension.",

    scorecard: [
      {
        dimension: "AI & Content Tools",
        score: 7,
        rationale:
          "Uses ChatGPT Plus for prompting, Envato AI Labs for high-quality video generation (describes near-4K quality), MidJourney for images, Kling, and Sora — with informed opinions on each tool's strengths and weaknesses (\"Sora has the best prompter but worst prompt-following\"). Also uses Gemini. Shows practical, evaluated toolchain selection rather than casual dabbling. Less breadth than Daniel on dedicated AI video platforms, but clearly an active and thoughtful AI practitioner.",
      },
      {
        dimension: "Core Platforms & Publishing Ops",
        score: 8,
        rationale:
          "Advanced use of Meta Business Suite, YouTube Studio Creator (performance analytics, monetization), Instagram, and Facebook per CV. Has managed Zoho Social for content scheduling and analytics. WordPress experience (web data entry and content migration for Kenneth Copeland Ministries). 5+ years of continuous, multi-platform content publishing for real audiences — YouTube channel, Facebook, Instagram Reels, and Shorts. This is sustained, operational publishing experience across the full stack.",
      },
      {
        dimension: "Performance & Lead Gen Mindset",
        score: 8,
        rationale:
          "Describes a clear three-layer content framework: Attention (hook + relatable problem) → Authority (education + expertise) → Trust/Conversion (real results, testimonials, transformation). References a concrete, measurable result: content strategy that drove 4,000 attendees to a live event against a 3,500 goal. Uses Google Analytics and Meta Analytics to diagnose funnel drop-off and identifies specific failure points (landing page design issue by geography). Connects content performance directly to lead channels — landing pages, WhatsApp, Instagram DMs — and flags the cost of unclear conversion channels from real experience.",
      },
      {
        dimension: "Short-Form Video Production & Editing",
        score: 7,
        rationale:
          "Has edited and published Reels, Shorts, and Stories for Kenneth Copeland Ministries Español from 2021 to 2025 — consistently, at scale, across multiple platforms. Uses Premiere Pro and Audition professionally. Mentions motion graphics-style elements (quick, bright post-production assets to stop scroll) as part of his hook strategy. Solid and sustained short-form video production experience, though not at the cinematic post-production level of Daniel or Juan Felipe.",
      },
      {
        dimension: "Storytelling, Hooks & Audience Psychology",
        score: 9,
        rationale:
          "The strongest storytelling thinker in this candidate pool. Opens the interview with a three-question framework: What problem is the audience solving? What story will make them stop scrolling? How do we turn that story into trust? Describes transformation narrative structure (problem → process → result) as the core engine for remodeling content. Unprompted, maps this directly to the client's context (renovation, before/after, homeowners imagining the outcome for their space). Uses vivid, specific hook examples (car crash → plot twist → renovation CTA) to show he thinks in actual script beats, not abstract theory.",
      },
      {
        dimension: "Search Intent & Topic Research",
        score: 7,
        rationale:
          "Explicitly references using Zoho Social for keyword research across major platforms — specifically checks viral content AND whether that content actually generates leads, not just views (\"if they don't have leads with that impact, they are not a reference for me\"). Also mentions YouTube hashtag trending, Meta, and being \"SEO driven\" as a default content planning practice. This is the most operationally specific topic research answer in the candidate pool — he distinguishes vanity virality from lead-driving content, which is exactly Manny's concern.",
      },
      {
        dimension: "Systemic Thinking & Content Engine Design",
        score: 8,
        rationale:
          "Describes a clear 90-day content engine build: research phase → consistent production flow → optimization loop with analytics. References identifying competitor best practices, refining hooks and CTAs based on performance data, and ensuring conversion channels are functional before scaling. Has sustained 5+ years of continuous content operations for a real organization (Iglesia Palabras de Fe y Vida) across YouTube, Facebook, and Instagram — weekly publishing, live broadcasts, short-form, and promotional content. This is not campaign-level experience; it is operational content engine management at scale.",
      },
      {
        dimension: "Communication, Ownership & Collaboration",
        score: 9,
        rationale:
          "Communicates with exceptional clarity, strategic structure, and fluency in English. Answers are organized into numbered frameworks, includes concrete examples with measurable outcomes, and anticipates follow-up questions. Bilingual (English/Spanish) with demonstrated professional use in international contexts (Kenneth Copeland Ministries Latin America, Linka AI remote English-language project). Shows ownership mindset throughout — proposes his own 90-day plan, identifies risks proactively (conversion channel failures from experience), and speaks as a strategic partner rather than a task-taker.",
      },
      {
        dimension: "Time Zone & Overlap Fit",
        score: 8,
        rationale:
          "Based in Bogotá, Colombia — UTC-5 year-round, same as or very close to U.S. Eastern. Full working-day overlap with Manny's 8am–8pm ET window is seamless. Colombia does not observe DST, making the overlap consistent and predictable year-round.",
      },
    ],

    strengths: [
      {
        headline: "Strongest storytelling and narrative framework in the candidate pool",
        description:
          "Juan Esteban thinks in lead-generating narrative structures from the first answer: hook → relatable problem → transformation → solution. He immediately maps this to remodeling content: show the before, the process, and the result so homeowners can imagine the outcome for their own space. This is precisely the content philosophy Manny's business needs to attract $50K–$1M project clients who need to trust and visualize before committing.",
      },
      {
        headline: "Proven content-driven event lead generation with measurable results",
        description:
          "Drove 4,000 attendees against a 3,500 goal using storytelling-led social content for Kenneth Copeland Ministries — and can describe exactly how the content was structured to create emotional identification and desire to participate, not just awareness. This is the clearest evidence in the pool of content directly causing a measurable conversion outcome at scale.",
      },
      {
        headline: "SEO-driven topic research with a lead quality filter built in",
        description:
          "Uses Zoho Social for keyword and topic research, but critically screens viral content against whether it also generates leads — dismissing high-reach, low-conversion references as \"virtual audience, not real audience.\" This mindset is directly aligned with Manny's core frustration: spending $100K on marketing that got views but not $1M projects.",
      },
      {
        headline: "Sustained operational content engine experience — 5+ years",
        description:
          "Has managed the full content pipeline for Iglesia Palabras de Fe y Vida since 2020: weekly programming, live streams, short-form Reels and Shorts, promotional content, event coverage — continuously, across YouTube, Facebook, and Instagram. This is not a project or campaign; it is long-term content operations, which maps directly to the consistent publishing cadence and content library Manny needs to build.",
      },
      {
        headline: "Understands conversion channel failure as a lead-gen risk",
        description:
          "Unprompted, describes a real experience where great content failed to generate leads because the landing page had a geographic configuration error — diagnosed via Google Analytics and Meta Analytics. This shows he thinks end-to-end: content → CTA → channel → conversion → analytics, not just content → post. For Manny, who has been burned by agencies that produced pretty content with no measurable return, this systems-level thinking is a meaningful differentiator.",
      },
      {
        headline: "WordPress and content management experience",
        description:
          "CV includes direct WordPress content management and migration work (Kenneth Copeland Ministries web assistant). This aligns with Manny's blueprint, which requires blog drafts to be published and staged on a WordPress site for approval.",
      },
    ],

    flags: [
      {
        headline: "Commercial local services lead generation is under-evidenced",
        description:
          "The bulk of Juan Esteban's lead-generation experience is in the non-profit/ministry context — event registrations, community building, institutional communications. Driving inbound inquiries from homeowners and developers for high-ticket local renovation services is a different dynamic: higher skepticism, more research-heavy buyer journey, stronger need for proof of past work and scope. This is a manageable ramp, given his strong strategic instincts, but it is worth setting expectations on domain adaptation time.",
      },
      {
        headline: "AI video generation toolchain is solid but mid-tier",
        description:
          "Mentions Envato AI Labs, MidJourney, Kling, and Sora. Envato AI Labs is less specialized than Runway or Kling for cinematic video generation, and his AI video stack is less deep than Daniel's. For social-native short-form content this is entirely adequate; for premium-quality AI-augmented showcase videos of large remodeling projects it may require supplementing or upgrading his toolchain over time. Not a current constraint but worth monitoring.",
      },
      {
        headline: "Post-production craft depth is below Daniel and Juan Felipe",
        description:
          "While competent in Premiere Pro and Audition, Juan Esteban's technical editing ceiling is not at the color grading, motion graphics, and compositing level of the other two production-focused candidates. For high-production-value cinematic content showcasing $500K+ commercial build-outs, his output quality would be professional and consistent but not premium-agency level. Manageable at the current scope of the role.",
      },
    ],

    when_to_hire: [
      "You want a single Content Producer who can independently hold both the strategy and execution layers — researching topics, structuring narrative, writing hooks and scripts, producing short-form video, publishing across platforms, and analyzing performance — without requiring a separate strategist to drive direction.",
      "You value demonstrated content-to-conversion thinking, systems-level lead-gen mindset, and strategic ownership over elite-level post-production craft.",
      "You want someone who can propose their own 90-day content engine plan, identify failure points proactively, and operate as a true partner to Manny and the AI Architect rather than a task-executor waiting for briefs.",
      "Long-term reliability and commitment matter: his 5+ year continuous output for a single organization signals he is a builder, not a job-hopper, which aligns with Manny's stated desire for long-term hires.",
      "Bilingual English/Spanish content production is valuable for reaching both English-speaking developers and Spanish-speaking homeowners in Manny's local market.",
    ],

    when_to_be_cautious: [
      "Cinematic, premium-grade video production with color grading, motion graphics, and compositing is the immediate top priority from day one — Juan Felipe Useche is the stronger choice for that specific need.",
      "You need heavy AI video generation workflows with platform-level specialization (Runway, Kling, Midjourney for image-to-video pipelines) to produce high-volume AI-augmented content at scale.",
      "The role will be purely execution-focused with all strategy provided externally — Juan Esteban's value is maximized when given strategic ownership, and constraining him to pure production execution would underutilize his strongest capabilities.",
    ],

    bottom_line:
      "Juan Esteban is the most complete and well-rounded Content Producer in this candidate pool. He combines the strongest storytelling instincts, a genuine lead-gen mindset, SEO-aware topic research habits, sustained content engine operations experience, and excellent English communication into a single profile. His production craft is solid but not elite, and his commercial local-services experience requires a ramp. As a standalone Content Producer for the full role as scoped — strategy, production, distribution, and performance — he is the top-ranked candidate in this pool.",
  };

  // Score pill helper
  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  const weightedAvg = (
    technicalAssessment.scorecard.reduce((sum, r) => sum + r.score, 0) /
    technicalAssessment.scorecard.length
  ).toFixed(1);

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <img
              src={JuanEstebanPhoto}
              alt={candidateProfileCopy.name}
              className="relative w-36 h-36 rounded-full object-cover border-4 border-[#36D1FF] shadow-lg"
            />
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Video Intro */}
          {candidateProfileCopy.loom_video_link && (
            <div className="w-full mt-4">
              <a
                href={candidateProfileCopy.loom_video_link}
                target="_blank"
                rel="noreferrer"
                className="block w-full text-center font-semibold py-3 rounded-lg
                           bg-[#36D1FF] text-[#01014C]
                           hover:bg-[#5FE2FF]
                           transition-all duration-300"
              >
                🎥 Watch Interview
              </a>
            </div>
          )}

          {/* Working Style */}
          <div className="bg-[#36D1FF]/5 border border-[#36D1FF]/20 p-4 rounded-xl text-sm text-[#E0F7FF] mb-6 mt-6">
            {candidateProfileCopy.working_style}
          </div>

          {/* Tools */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Tools
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-3">
                <p className="text-xs text-[#E0F7FF]/80 mb-1">{group.category}</p>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((tool, j) => (
                    <span
                      key={j}
                      className="bg-[#36D1FF]/10 text-[#36D1FF] text-xs px-2 py-1 rounded-full border border-[#36D1FF]/30"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Soft Skills
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI-Architechs Logo" className="w-42 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* Strengths */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flags */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">
                          {row.dimension}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ScorePill score={row.score} />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">
                          {row.rationale}
                        </td>
                      </tr>
                    ))}
                    {/* Weighted Average Row */}
                    <tr className="bg-[#01014C]/5 font-semibold">
                      <td className="px-4 py-3 text-[#01014C]">Weighted Average</td>
                      <td className="px-4 py-3">
                        <ScorePill score={parseFloat(weightedAvg)} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm italic">
                        Average across all 9 dimensions
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Considerations */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* When to Hire */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire Juan Esteban if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* When to Be Cautious */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}
